/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================================================
// Currency Formatting with Dynamic Cache
// ============================================================================

/**
 * Currency data structure matching CurrencyDto from backend
 */
interface CurrencyData {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
}

/**
 * Currency cache populated from API
 * Key: currency code (e.g., 'USD'), Value: currency data
 */
let currencyCache: Map<string, CurrencyData> = new Map();

/**
 * Set the currency cache with data from API
 * Call this after fetching currencies to enable proper formatting
 */
export function setCurrencyCache(currencies: CurrencyData[]): void {
  currencyCache = new Map(currencies.map(c => [c.code, c]));
}

/**
 * Get currency data from cache
 */
export function getCurrencyData(code: string): CurrencyData | undefined {
  return currencyCache.get(code);
}

/**
 * Get currency symbol from cache, or return the code if not found
 */
export function getCurrencySymbol(code: string): string {
  return currencyCache.get(code)?.symbol ?? code;
}

/**
 * Format currency amount with proper decimal places and symbol
 * Uses cached currency data for dynamic formatting
 *
 * @param amount - The amount to format
 * @param currencyCode - ISO currency code (e.g., 'USD', 'GNF')
 * @returns Formatted currency string (e.g., '$1,234.56', 'GNF 1,234')
 */
export function formatCurrency(amount: number, currencyCode = 'USD'): string {
  const currency = currencyCache.get(currencyCode);
  const decimalPlaces = currency?.decimalPlaces ?? 2;
  const symbol = currency?.symbol ?? currencyCode;

  // Format the number with proper decimal places
  const formattedNumber = amount.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  // Use symbol-first format for common currencies, code-first for others
  const symbolFirst = ['$', '€', '£', '¥', '₦', 'R'].includes(symbol);

  return symbolFirst
    ? `${symbol}${formattedNumber}`
    : `${symbol} ${formattedNumber}`;
}

/**
 * Format currency using Intl.NumberFormat (fallback method)
 * Uses the browser's built-in currency formatting
 */
export function formatCurrencyIntl(amount: number, currencyCode = 'USD', locale = 'en-US'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  } catch {
    // Fallback if currency code is not supported by Intl
    return formatCurrency(amount, currencyCode);
  }
}

/**
 * JWT Token Utilities
 */

interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decode a JWT token without verification (for client-side expiry check only)
 * Note: This does NOT verify the signature - that's the backend's job
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle base64url encoding (replace - with + and _ with /)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token The JWT token string
 * @param bufferSeconds Optional buffer in seconds before actual expiry (default: 30)
 * @returns true if expired or invalid, false if still valid
 */
export function isJwtExpired(token: string | null, bufferSeconds = 30): boolean {
  if (!token) return true;

  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return true;

  // exp is in seconds, Date.now() is in milliseconds
  const expiresAt = payload.exp * 1000;
  const bufferMs = bufferSeconds * 1000;

  return Date.now() >= (expiresAt - bufferMs);
}

/**
 * Get the expiry time of a JWT token
 * @param token The JWT token string
 * @returns Expiry timestamp in milliseconds, or null if invalid
 */
export function getJwtExpiry(token: string | null): number | null {
  if (!token) return null;

  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return null;

  return payload.exp * 1000;
}
